import { sanitizeProfile } from './sanitize';

const BLOCK_TAGS = new Set([
  'ADDRESS',
  'ARTICLE',
  'ASIDE',
  'BLOCKQUOTE',
  'DD',
  'DIV',
  'DL',
  'DT',
  'FIELDSET',
  'FIGCAPTION',
  'FIGURE',
  'FOOTER',
  'FORM',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HEADER',
  'HR',
  'LI',
  'MAIN',
  'NAV',
  'OL',
  'P',
  'PRE',
  'SECTION',
  'TABLE',
  'UL',
]);

function escapeHtml(value) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escapeAttribute(value) {
  return escapeHtml(value).replace(/"/g, '&quot;');
}

function isBreak(value) {
  return /^<br\s*\/?>$/i.test(value);
}

function appendBreak(parts) {
  if (parts.length === 0 || !isBreak(parts[parts.length - 1])) {
    parts.push('<br>');
  }
}

function serializeNodes(nodes) {
  const parts = [];

  nodes.forEach((node, index) => {
    if (node.nodeType === Node.TEXT_NODE) {
      parts.push(escapeHtml(node.textContent || ''));
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    if (node.tagName === 'BR') {
      parts.push('<br>');
      return;
    }

    const childHtml = serializeNodes(Array.from(node.childNodes));

    if (BLOCK_TAGS.has(node.tagName)) {
      if (parts.length > 0) {
        appendBreak(parts);
      }
      if (childHtml) {
        parts.push(childHtml);
      } else {
        appendBreak(parts);
      }
      if (index < nodes.length - 1) {
        appendBreak(parts);
      }
      return;
    }

    const tagName = node.tagName.toLowerCase();
    const attributes = Array.from(node.attributes)
      .map((attribute) => `${attribute.name}="${escapeAttribute(attribute.value)}"`)
      .join(' ');
    parts.push(`<${tagName}${attributes ? ` ${attributes}` : ''}>${childHtml}</${tagName}>`);
  });

  return parts.join('');
}

// Parses into an inert template document. A detached element is not inert:
// assigning innerHTML there still fires resource loads, so untrusted pasted
// markup could hit a tracking pixel before the sanitizer strips it. A template
// is inert and still parses as a fragment, so whitespace handling is unchanged.
function parseInert(html) {
  const template = document.createElement('template');
  template.innerHTML = html;
  return template;
}

export function normalizeEditorHtml(html) {
  if (!html) return '';

  const parsed = parseInert(html);
  const sanitized = sanitizeProfile(serializeNodes(Array.from(parsed.content.childNodes)));

  // Re-read from the DOM so the result is byte-identical to what the browser
  // will serialize (e.g. `<br>`, not the sanitizer's `<br />`). Without this,
  // every comparison against a live `innerHTML` looks like a change.
  return parseInert(sanitized).innerHTML;
}

export function plainTextToEditorHtml(text) {
  return escapeHtml(text).replace(/\r\n|\r|\n/g, '<br>');
}
